/**
 * The URL allowlist of every link the app renders (roadmap §3.6, fixes F-08): same-origin paths, in-page
 * fragments and https URLs only. javascript:, data:, vbscript:, http: and protocol-relative (//host) links are refused,
 * as are URLs with credentials or control characters. Returns the href to use, or null.
 */
export function safeHref(raw: string, origin: string = globalThis.location?.origin ?? 'http://localhost'): string | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > 2048) return null;
  if ([...raw].some((ch) => ch.charCodeAt(0) < 0x20 || ch.charCodeAt(0) === 0x7f)) return null;
  const s = raw.trim();
  if (s !== raw) return null;

  if (s.startsWith('#')) return /^#[A-Za-z][\w-]{0,63}$/.test(s) ? s : null;   // an in-page target (skip link)

  if (s.startsWith('/')) {
    if (s.startsWith('//') || s.startsWith('/\\')) return null;
    let u: URL;
    try {
      u = new URL(s, origin);
    } catch {
      return null;
    }
    if (u.origin !== origin) return null;
    return `${u.pathname}${u.search}${u.hash}`;
  }

  let u: URL;
  try {
    u = new URL(s);
  } catch {
    return null;
  }
  if (u.protocol !== 'https:' || u.username !== '' || u.password !== '') return null;
  return u.href;
}

/** A link that leaves the app (opens in a new tab with noopener/noreferrer). */
export function isExternalHref(href: string, origin: string = globalThis.location?.origin ?? 'http://localhost'): boolean {
  return !href.startsWith('/') && !href.startsWith('#') && new URL(href).origin !== origin;
}
